<script setup lang="ts">
import { CheckboxIndicator, CheckboxRoot, useForwardPropsEmits } from 'reka-ui'
import type { CheckboxRootEmits, CheckboxRootProps } from 'reka-ui'
import { Check } from 'lucide'
import type { HTMLAttributes } from 'vue'
import SphIcon from '@/components/base/icon'
import { cn } from '@/utils/shadcn/lib/utils.ts'
import { reactiveOmit } from '@vueuse/core'

defineOptions({
    compatConfig: { MODE: 3 },
})

const props = defineProps<CheckboxRootProps & {
    class?: HTMLAttributes['class']
    invalid?: boolean
}>()
const emits = defineEmits<CheckboxRootEmits>()

const delegatedProps = reactiveOmit(props, 'class', 'invalid')

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
    <CheckboxRoot
        v-slot="slotProps"
        data-slot="checkbox"
        v-bind="forwarded"
        :aria-invalid="props.invalid ? 'true' : 'false'"
        class="sph-form-control sph-checkbox"
        :class="cn('bg-input peer border-input-dark data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary size-6! shrink-0 rounded-sm border shadow-xs transition-shadow transition-colors outline-none',
                   props.class)"
    >
        <CheckboxIndicator
            data-slot="checkbox-indicator"
            class="grid place-content-center text-current transition-none"
            :force-mount="true"
        >
            <slot v-bind="slotProps">
                <SphIcon
                    :icon="Check"
                    size="sm"
                />
            </slot>
        </CheckboxIndicator>
    </CheckboxRoot>
</template>

<style scoped>
.sph-checkbox :deep(svg) {
    --sph-icon-stroke-width:2;

    stroke-dasharray: 23;
    stroke-dashoffset: 23;
    stroke: #fff;
    opacity: 0;
    transform: translate(0%, -20%);
    transition: stroke-dashoffset 0s, transform 0s, opacity 0.25s;
}

.sph-checkbox :deep([data-state=checked] svg) {
    stroke-dashoffset: 46;
    opacity: 1;
    transform: translate(0, 0);
    transition: stroke-dashoffset 0.2s .1s var(--ease-out-sine), transform .4s var(--ease-out-cubic), opacity 0s;
}
</style>
